import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * Oblique Design System — OverflowTooltip (HubSpot-Style Cell Expansion)
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * When cell text is truncated (CSS ellipsis), this component renders an
 * elevated surface card anchored precisely over the cell's position — 
 * matching HubSpot's "cell grows in place" pattern.
 * 
 * Key behaviors:
 * - Detects DOM text truncation via `scrollWidth > clientWidth`
 * - If text is 100% visible, the popover NEVER appears
 * - Popover anchors to cell's exact top-left, covering the original cell
 * - White/surface card with shadow — same font styling as the cell
 * - No caret/arrow — clean elevated card look
 * - Auto-clamps to viewport edges
 * - Renders via React Portal to escape overflow: hidden containers
 */

export function OverflowTooltip({
    children,
    text,
    content,
    position = 'top',
    delay = 120,
    onlyWhenOverflow = true,
    highlightCell = true,
    className = '',
    tooltipClassName = '',
    maxWidth = 420
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0, width: 0, height: 0 });
    const triggerRef = useRef(null);
    const timerRef = useRef(null);
    const popoverRef = useRef(null);

    const tooltipContent = content || text || (typeof children === 'string' ? children : null);

    // Measure whether the content is truncated
    const checkOverflow = useCallback(() => {
        if (!onlyWhenOverflow) return true;
        const el = triggerRef.current;
        if (!el) return false;

        // 1. Check the container itself
        if (el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1) {
            return true;
        }

        // 2. Check inner child (e.g. span with natural text width vs parent clientWidth)
        const child = el.firstElementChild;
        if (child) {
            if (child.scrollWidth > child.clientWidth + 1) return true;
            if (child.scrollWidth > el.clientWidth + 1) return true;
            if (child.scrollHeight > child.clientHeight + 1) return true;
        }

        return false;
    }, [onlyWhenOverflow]);

    const updatePosition = useCallback(() => {
        if (!triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        
        // Anchor to cell's exact position
        let targetLeft = rect.left;
        let targetTop = rect.top;

        // Clamp: if card would overflow right edge of viewport, shift left
        const maxLeft = window.innerWidth - maxWidth - 12;
        if (targetLeft > maxLeft) {
            targetLeft = Math.max(8, maxLeft);
        }

        // Clamp: if card would overflow bottom, shift up slightly
        if (targetTop + rect.height > window.innerHeight - 16) {
            targetTop = window.innerHeight - rect.height - 16;
        }

        setCoords({
            top: targetTop,
            left: targetLeft,
            width: rect.width,
            height: rect.height,
            cellRight: rect.right,
            cellBottom: rect.bottom
        });
    }, [maxWidth]);

    const handleMouseEnter = () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        
        // Only trigger if overflowing
        if (checkOverflow()) {
            timerRef.current = setTimeout(() => {
                updatePosition();
                setIsOpen(true);
            }, delay);
        }
    };

    const handleMouseLeave = (e) => {
        if (timerRef.current) clearTimeout(timerRef.current);
        
        // If mouse moves to the popover, keep it open
        const relatedTarget = e.relatedTarget;
        if (popoverRef.current && popoverRef.current.contains(relatedTarget)) {
            return;
        }
        
        setIsOpen(false);
    };

    const handlePopoverMouseLeave = (e) => {
        // If mouse moves back to trigger, keep it open
        const relatedTarget = e.relatedTarget;
        if (triggerRef.current && triggerRef.current.contains(relatedTarget)) {
            return;
        }
        setIsOpen(false);
    };

    // Auto-close on scroll or window resize to prevent floating detached popovers
    useEffect(() => {
        if (!isOpen) return;
        const handleScrollOrResize = () => setIsOpen(false);
        window.addEventListener('scroll', handleScrollOrResize, true);
        window.addEventListener('resize', handleScrollOrResize);
        return () => {
            window.removeEventListener('scroll', handleScrollOrResize, true);
            window.removeEventListener('resize', handleScrollOrResize);
        };
    }, [isOpen]);

    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    return (
        <>
            <div
                ref={triggerRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className={`relative inline-block max-w-full overflow-hidden text-ellipsis whitespace-nowrap transition-all duration-150 ${
                    highlightCell && isOpen ? 'ring-1 ring-[var(--accent)]/30 rounded-[3px]' : ''
                } ${className}`}
            >
                {children || text}
            </div>

            {typeof document !== 'undefined' && createPortal(
                <AnimatePresence>
                    {isOpen && tooltipContent && (
                        <motion.div
                            ref={popoverRef}
                            initial={{ opacity: 0, scale: 0.97 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.98 }}
                            transition={{ duration: 0.1, ease: [0.16, 1, 0.3, 1] }}
                            onMouseLeave={handlePopoverMouseLeave}
                            style={{
                                position: 'fixed',
                                top: coords.top,
                                left: coords.left,
                                minWidth: `${coords.width}px`,
                                minHeight: `${coords.height}px`,
                                maxWidth: `${maxWidth}px`,
                                zIndex: 99999,
                                pointerEvents: 'auto'
                            }}
                            className={`
                                px-2.5 py-1.5 rounded-md 
                                text-[13px] font-medium tracking-normal leading-[1.4]
                                text-[var(--text-primary)] 
                                bg-[var(--bg-surface)] 
                                border border-[var(--border)]
                                shadow-[0_4px_16px_rgba(0,0,0,0.12),0_1px_3px_rgba(0,0,0,0.08)]
                                dark:shadow-[0_4px_20px_rgba(0,0,0,0.35),0_1px_4px_rgba(0,0,0,0.2)]
                                whitespace-normal break-words
                                select-text
                                font-['Lexend_Deca',sans-serif]
                                ${tooltipClassName}
                            `}
                        >
                            <span>{tooltipContent}</span>
                        </motion.div>
                    )}
                </AnimatePresence>,
                document.body
            )}
        </>
    );
}

export default OverflowTooltip;
